/**
 * `commandUiSlice` — the input line's presentation state (architecture.md
 * §8): what is being typed and where history browsing points.
 *
 * @remarks
 * D79 moved the question itself into `RuntimeState.session.pendingQuestion`
 * — it is conversation, and saves/replays carry it — so this slice never
 * modeled it. The presentation-only DISMISSAL flag that used to live here is
 * gone too (R34/D114): dismissing the chip now submits the withdrawal words
 * (`never mind`) through the engine, so the question actually closes, the
 * transcript records the gesture, and replays folding raw inputs reproduce
 * it — the flag was a second mechanism telling a different story than the
 * state (Calder's §7.1: a control that lies).
 *
 * `historyCursorIndex` indexes into `commandHistory` (older = smaller);
 * `undefined` means not browsing. The slice stores the cursor only — the
 * VALUE it points at is derived via selector (no derived data in state,
 * §3.2's spirit).
 *
 * @packageDocumentation
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** The input line's state. */
export interface CommandUiSliceState {
	/** Where history browsing points; `undefined` when not browsing. */
	readonly historyCursorIndex: number | undefined;
	/** The live input value (controlled input). */
	readonly inputValue: string;
}

const initialState: CommandUiSliceState = {
	historyCursorIndex: undefined,
	inputValue: '',
};

/** The command-line UI slice — see the module remarks. */
export const commandUiSlice = createSlice({
	initialState,
	name: 'commandUi',
	reducers: {
		/** Point history browsing somewhere (`undefined` = stop browsing). */
		historyCursorChanged: (sliceState, action: PayloadAction<number | undefined>) => ({
			...sliceState,
			historyCursorIndex: action.payload,
		}),
		/** An input was submitted: clear the line, stop browsing. */
		inputSubmitted: () => initialState,
		/** The player typed — browsing stops, the value changes. */
		inputValueChanged: (sliceState, action: PayloadAction<string>) => ({
			...sliceState,
			historyCursorIndex: undefined,
			inputValue: action.payload,
		}),
	},
});

export const { historyCursorChanged, inputSubmitted, inputValueChanged } = commandUiSlice.actions;
